"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";

type CreateProjectInput = {
  organizationId: string;
  name: string;
  kind: string;
  color: string;
  description?: string;
  // SA/UA 共通: BigQuery 設定 ID
  bigQueryId?: string;
  // UA のみ: キーイベント
  keyEvents?: string[];
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

const GetBQQuery = graphql(`
  query GetBQ($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsBigQuery {
        id
        projectID
        datasetName
      }
      ... on UserAnalyticsBigQuery {
        id
        projectID
        datasetName
      }
    }
  }
`);

export function CreateProjectConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const input = (part.input ?? {}) as Partial<CreateProjectInput>;
  const color = typeof input.color === "string" ? input.color : "#999";

  // SA/UA 種別
  const isSA = input.kind === "SEARCH_ANALYTICS" || input.kind === "sa";
  const isUA = input.kind === "USER_ANALYTICS" || input.kind === "ua";

  const [{ data, error }] = useQuery({
    query: GetBQQuery,
    variables: { id: input.bigQueryId ?? "" },
    pause: !input.bigQueryId,
  });

  const selectedBqLabel =
    data?.node?.__typename === "SearchAnalyticsBigQuery"
      ? `${data.node.projectID} - ${data.node.datasetName}`
      : data?.node?.__typename === "UserAnalyticsBigQuery"
        ? `${data.node.projectID} - ${data.node.datasetName}`
        : error?.message;

  if (!(isToolUIPart(part) && getToolName(part) === "create_project"))
    return null;
  if (part.state !== "input-available") return null;

  if (error) {
    return (
      <div className="p-4 text-sm space-y-4">
        <Alert variant="destructive">
          <AlertDescription>
            <Trans>
              The specified BigQuery configuration could not be found. Please
              cancel and try again — Amethyst AI will look up the available
              configurations for you.
            </Trans>
          </AlertDescription>
        </Alert>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    );
  }

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to create a new project?</Trans>
      </div>
      <ul className="mb-4 list-disc pl-5 space-y-1">
        <li>
          <Trans>Name: </Trans>
          <span className="font-medium">{input.name}</span>
        </li>
        <li>
          <Trans>Project Type: </Trans>
          <span className="font-mono">{input.kind}</span>
        </li>
        {input.bigQueryId && (isSA || isUA) && (
          <li>
            <Trans>BigQuery dataset: </Trans>
            <span className="font-mono">{selectedBqLabel}</span>
          </li>
        )}
        {isUA &&
          Array.isArray(input.keyEvents) &&
          input.keyEvents.length > 0 && (
            <li>
              <Trans>Key events: </Trans>
              <span className="font-mono">{input.keyEvents.join(", ")}</span>
            </li>
          )}
        <li>
          <Trans>Color: </Trans>
          {input.color ? (
            <>
              <span
                aria-label="color preview"
                className="inline-block size-4 rounded border"
                style={{ backgroundColor: color }}
              />
              <span className="font-mono">{input.color}</span>
            </>
          ) : (
            <Trans>Random</Trans>
          )}
        </li>
        {input.description && <li>説明: {input.description}</li>}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Create</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
